import React, { useState, useRef, useEffect } from 'react';
import { Form, Overlay, Spinner, Popover } from 'react-bootstrap';
import CreateNewEntryButton from './CreateNewEntryButton';
import simpleLogo from '../../resources/logo-simple.png';
import { EntryBlockWithContext } from '../../components/calendar/types';
import { getDateStringFromCreationDate } from '../../util/dateTime';
import { loadExistingEntry } from '../../components/calendar/calendar';
import { useHistory } from 'react-router-dom';

const AssistantPill = (props: { value: string; context: string[]; conversation: EntryBlockWithContext[] }) => {
  const { value, context, conversation } = props;
  const [show, setShow] = useState(false);
  const [target, setTarget] = useState(null);
  const [loading, setLoading] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const handleClick = (event: any) => {
    setShow(!show);
    setTarget(event.target);
  };
  const history = useHistory();

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        setShow(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return context.length > 0 ? (
    <div ref={ref}>
      <button
        onClick={handleClick}
        className="tw-flex tw-py-2 tw-px-3 tw-items-center tw-gap-4 tw-rounded-lg tw-bg-primary-100 tw-border-none tw-text-base tw-text-left tw-max-w-[42rem]"
      >
        {value}
      </button>
      <Overlay show={show} target={target} placement="right" container={ref} containerPadding={20}>
        <Popover id="popover-contained">
          <Popover.Header as="h3">Entries used in this response</Popover.Header>
          <Popover.Body>
            {loading ? (
              <Spinner animation="border" />
            ) : (
              [...new Set(context.map((i) => i.split('-')[0]))]
                .sort((a, b) => Number(a) - Number(b))
                .map((entry, i) => {
                  return (
                    <div key={i}>
                      <button
                        disabled={loading}
                        className="tw-text-base tw-underline tw-bg-transparent tw-border-none"
                        onClick={async () => {
                          try {
                            setLoading(true);
                            sessionStorage.setItem('conversation', window.btoa(JSON.stringify(conversation)));
                            const le = await loadExistingEntry({
                              entryName: entry,
                              setOpeningEntry: () => {}
                            });
                            const entryState = [
                              {
                                ...le,
                                name: entry
                              }
                            ];
                            setLoading(false);
                            return history.push({
                              pathname: `/entry/${entry}`,
                              state: {
                                entryState
                              }
                            });
                          } catch (error) {
                            setLoading(false);
                            console.log(error);
                            window.alert(
                              'Error loading entry. Please try again. If this issue persists, please contact us at support@jumblejournal.org'
                            );
                          }
                        }}
                      >
                        {getDateStringFromCreationDate(entry)}
                      </button>
                    </div>
                  );
                })
            )}
          </Popover.Body>
        </Popover>
      </Overlay>
    </div>
  ) : (
    <div className="tw-flex tw-py-2 tw-px-3 tw-items-center tw-gap-4 tw-rounded-lg tw-bg-primary-100 tw-border-none tw-text-base tw-max-w-[42rem]">
      {value}
    </div>
  );
};

const UserPill = (props: { value: string; key: number }) => {
  const { value, key } = props;
  return (
    <div
      key={key}
      className="tw-flex tw-py-2 tw-px-3 tw-items-center tw-gap-4 tw-self-end tw-rounded-lg tw-bg-primary-500 tw-text-white tw-border-none tw-text-base tw-font-semibold tw-max-w-[42rem]"
    >
      {value}
    </div>
  );
};

const GptPromptCard = (props: {
  conversation: EntryBlockWithContext[];
  handleUserQuery: (conversation: EntryBlockWithContext[]) => Promise<void>;
  clearConversation: () => void;
  isLoadingEntries?: boolean;
  navigateToDailyEntry?: () => void;
  customClass?: string;
  placeholder?: string;
}) => {
  const {
    navigateToDailyEntry,
    isLoadingEntries,
    conversation,
    clearConversation,
    handleUserQuery,
    customClass,
    placeholder
  } = props;

  const [userQuery, setUserQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const convoBoxRef = useRef<HTMLDivElement | null>(null);

  // scroll to top
  useEffect(() => {
    if (convoBoxRef.current) {
      const div = convoBoxRef.current;
      smoothScrollTo(div, div.scrollHeight - div.clientHeight, 500); // 500ms animation
    }
  }, []);

  const handleNewPrompt = async () => {
    if (!userQuery) {
      window.alert('The prompt cannot be empty 🫠⁭');
      return;
    }

    try {
      setLoading(true);
      const newConversation = [
        ...conversation,
        { role: 'user', content: userQuery, context: [] } as EntryBlockWithContext
      ];
      await handleUserQuery(newConversation);
      setUserQuery('');
    } catch (error) {
      window.alert('Error connecting to AI. Please try again. Sometimes the servers are slow 🐢');
    }
    setLoading(false);
  };

  return (
    <div
      className={`tw-bg-white tw-rounded-3xl tw-shadow-md tw-flex tw-flex-col tw-h-full tw-w-full tw-overflow-hidden ${
        customClass || ''
      }`}
    >
      <div className="tw-flex tw-flex-row tw-gap-2 tw-justify-end tw-items-center tw-px-6 tw-pt-4 tw-pb-2">
        <button
          disabled={loading}
          className="tw-px-3 tw-py-2 tw-rounded-lg tw-bg-secondary-200 tw-text-neutral-700 tw-border-none tw-text-xs tw-cursor-pointer"
          onClick={clearConversation}
        >
          clear
        </button>
        {navigateToDailyEntry && (
          <CreateNewEntryButton
            btnText="Journal from prompt"
            className="tw-px-3 tw-py-2 tw-rounded-lg tw-text-xs"
            navigateToDailyEntry={navigateToDailyEntry}
            disabled={loading || isLoadingEntries || conversation.length <= 3}
          />
        )}
      </div>

      <div
        className="tw-flex tw-px-6 tw-pb-4 tw-flex-col tw-items-start tw-gap-3 tw-self-stretch tw-overflow-y-auto tw-flex-1 tw-min-h-[10rem]"
        ref={convoBoxRef}
      >
        {conversation.map((item, i) => {
          return item.role === 'assistant' ? (
            <AssistantPill value={item.content} key={i} context={item.context} conversation={conversation} />
          ) : (
            <UserPill value={item.content} key={i} />
          );
        })}
        {loading && (
          <div className="tw-flex tw-flex-row tw-items-start tw-gap-4">
            <img
              style={{ opacity: '0.6', height: '24px', width: '24px' }}
              alt="logo"
              className="logo-img blinking-element"
              src={simpleLogo}
            ></img>
            <div className="tw-text-sm tw-text-primary-300">Thinking...</div>
          </div>
        )}
      </div>
      <div className="tw-p-4 tw-gap-3 tw-flex tw-items-stretch tw-flex-shrink-0 tw-self-stretch tw-bg-neutral-50 tw-border-t tw-border-neutral-100 sm:tw-flex-row tw-flex-col">
        <Form.Control
          onChange={(event) => {
            setUserQuery(event.target.value);
          }}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              handleNewPrompt();
            }
          }}
          className="tw-border tw-border-neutral-200 tw-bg-white tw-rounded-lg tw-px-3 tw-min-h-control tw-flex-1 tw-text-base"
          placeholder={placeholder || 'Write here...'}
          value={userQuery}
        />
        <button
          className="tw-flex tw-justify-center tw-items-center tw-gap-2 tw-px-6 tw-min-h-control tw-rounded-lg tw-bg-gradient-to-b tw-from-primary-400 tw-to-secondary-500 tw-text-white tw-border-none tw-text-base tw-font-semibold tw-cursor-pointer"
          disabled={loading}
          onClick={handleNewPrompt}
        >
          Generate
        </button>
      </div>
    </div>
  );
};

function smoothScrollTo(element: HTMLElement, target: number, duration: number) {
  const start = element.scrollTop;
  const change = target - start;
  let startTime: number | null = null;

  function animateScroll(currentTime: number) {
    if (startTime === null) startTime = currentTime;
    const elapsed = currentTime - startTime;

    const progress = elapsed / duration;

    if (progress < 1) {
      element.scrollTop = start + change * progress;
      window.requestAnimationFrame(animateScroll);
    } else {
      element.scrollTop = target;
    }
  }

  window.requestAnimationFrame(animateScroll);
}

const ChatWithJournalCard = (props: {
  conversation: EntryBlockWithContext[];
  handleUserQuery: (conversation: EntryBlockWithContext[]) => Promise<void>;
  clearConversation: () => void;
  isLoadingEntries?: boolean;
  navigateToDailyEntry?: () => void;
  placeholder?: string;
  /** A question to send on mount, so another page can start the conversation. */
  autoSubmitQuery?: string;
}) => {
  const {
    navigateToDailyEntry,
    isLoadingEntries,
    conversation,
    clearConversation,
    handleUserQuery,
    placeholder,
    autoSubmitQuery
  } = props;

  const [userQuery, setUserQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const convoBoxRef = useRef<HTMLDivElement | null>(null);

  // scroll to top
  useEffect(() => {
    if (convoBoxRef.current) {
      const div = convoBoxRef.current;
      smoothScrollTo(div, div.scrollHeight - div.clientHeight, 500); // 500ms animation
    }
  }, []);

  const submitQuery = async (query: string) => {
    if (!query) {
      window.alert('The prompt cannot be empty 🫠⁭');
      return;
    }

    try {
      setLoading(true);
      const newConversation = [...conversation, { role: 'user', content: query, context: [] } as EntryBlockWithContext];
      await handleUserQuery(newConversation);
      setUserQuery('');
    } catch (error) {
      window.alert('Error connecting to AI. Please try again. Sometimes the servers are slow 🐢');
    }
    setLoading(false);
  };

  const handleNewPrompt = () => submitQuery(userQuery);

  /*
   * Asking from the home page should feel like one action, so the question
   * arrives as route state and is sent here — the card owns the loading state
   * and the conversation, so it is the only place that can show it being asked.
   * The ref keeps a re-render from asking twice.
   */
  const autoSubmitted = useRef(false);
  const submitQueryRef = useRef(submitQuery);
  submitQueryRef.current = submitQuery;
  useEffect(() => {
    if (!autoSubmitQuery || autoSubmitted.current) {
      return;
    }
    autoSubmitted.current = true;
    submitQueryRef.current(autoSubmitQuery);
  }, [autoSubmitQuery]);

  return (
    <div className="tw-bg-white tw-rounded-3xl tw-flex tw-flex-col tw-h-full tw-w-full tw-overflow-hidden tw-shadow-[0px_5px_60px_20px_rgba(117,67,232,0.4)]">
      <div className="tw-flex tw-flex-row tw-gap-2 tw-justify-end tw-items-center tw-px-6 tw-pt-4 tw-pb-2">
        <button
          disabled={loading}
          className="tw-px-3 tw-py-2 tw-rounded-lg tw-bg-secondary-200 tw-text-neutral-700 tw-border-none tw-text-xs tw-cursor-pointer"
          onClick={clearConversation}
        >
          clear
        </button>
        {navigateToDailyEntry && (
          <CreateNewEntryButton
            btnText="Journal from prompt"
            className="tw-px-3 tw-py-2 tw-rounded-lg tw-text-xs"
            navigateToDailyEntry={navigateToDailyEntry}
            disabled={loading || isLoadingEntries || conversation.length <= 3}
          />
        )}
      </div>

      <div
        className="tw-flex tw-px-6 tw-pb-4 tw-flex-col tw-items-start tw-gap-3 tw-self-stretch tw-overflow-y-auto tw-flex-1 tw-min-h-0"
        ref={convoBoxRef}
      >
        {conversation.map((item, i) => {
          return item.role === 'assistant' ? (
            <AssistantPill value={item.content} key={i} context={item.context} conversation={conversation} />
          ) : (
            <UserPill value={item.content} key={i} />
          );
        })}
        {loading && (
          <div className="tw-flex tw-flex-row tw-items-start tw-gap-4">
            <img
              style={{ opacity: '0.6', height: '24px', width: '24px' }}
              alt="logo"
              className="logo-img blinking-element"
              src={simpleLogo}
            ></img>
            <div className="tw-text-sm tw-text-primary-300">Thinking...</div>
          </div>
        )}
      </div>
      <div className="tw-p-4 tw-gap-3 tw-flex tw-items-stretch tw-flex-shrink-0 tw-self-stretch tw-bg-neutral-50 tw-border-t tw-border-neutral-100 sm:tw-flex-row tw-flex-col">
        <Form.Control
          onChange={(event) => {
            setUserQuery(event.target.value);
          }}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              handleNewPrompt();
            }
          }}
          className="tw-border tw-border-neutral-200 tw-bg-white tw-rounded-lg tw-px-3 tw-min-h-control tw-flex-1 tw-text-base"
          placeholder={placeholder || 'Write here...'}
          value={userQuery}
        />
        <button
          className="tw-flex tw-justify-center tw-items-center tw-gap-2 tw-px-6 tw-min-h-control tw-rounded-lg tw-bg-gradient-to-b tw-from-primary-400 tw-to-secondary-500 tw-text-white tw-border-none tw-text-base tw-font-semibold tw-cursor-pointer"
          disabled={loading}
          onClick={handleNewPrompt}
        >
          Generate
        </button>
      </div>
    </div>
  );
};

export { GptPromptCard, ChatWithJournalCard };
